import React, { useMemo, useState } from "react";
import { Button } from "../../Button";
import { LOGO_ALT, TWILIO_SENDGRID_LOGO } from "../../constants";
import { signupContext } from "../../context";
import ErrorAlert from "../../ErrorAlert";
import { ExternalLink } from "../../external-navigate";
import { useFormState } from "../../hooks/useFormState";
import { SendGridClient } from "../../sendgrid-auth/client";
import { TextInput } from "../../TextInput";

export const tooManyUsernamesErrorMessage = (
  <p style={{ margin: "0 10px" }}>
    There are too many usernames associated with this email address. Please contact{" "}
    <a target="_blank" rel="noreferrer" href="https://support.sendgrid.com/hc/en-us/requests/new">
      SendGrid support
    </a>{" "}
    for assistance.
  </p>
);

export const invalidEmailErrorMessage = (
  <p style={{ margin: "0 10px" }}>
    The email address you entered is invalid or malformed. Please enter a valid email address.
  </p>
);

export const rateLimitErrorMessage = (
  <p style={{ margin: "0 10px" }}>Too many requests. Please try again later.</p>
);

export const internalServerErrorMessage = (
  <p style={{ margin: "0 10px" }}>An unexpected error occurred. Please try again later.</p>
);

export const successMessage = (
  <p style={{ margin: "0 10px" }}>
    If there are any usernames associated with this email address, we&apos;ve sent them to you.
    Please check your email.
  </p>
);

export default function ForgotUsername() {
  const [email, , changeEmail] = useFormState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | Error | JSX.Element | undefined>("");
  const [successBanner, setSuccessBanner] = useState<string | JSX.Element | undefined>("");

  const sendGridClient = useMemo(() => new SendGridClient({}), []);

  const formSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorBanner("");
    setSuccessBanner("");
    setIsLoading(true);

    try {
      const result = await sendGridClient.forgotUsername(email);

      if (result.success) {
        setSuccessBanner(successMessage);
        setIsLoading(false);
        return;
      }

      if (result.status === 400) {
        if (result.error_id === "ERR_TOO_MANY_USERNAMES") {
          setErrorBanner(tooManyUsernamesErrorMessage);
        } else if (result.error_id === "ERR_INVALID_EMAIL") {
          setErrorBanner(invalidEmailErrorMessage);
        } else {
          setErrorBanner(result.message || "An error occurred. Please try again.");
        }
        setIsLoading(false);
        return;
      }

      if (result.status === 429) {
        setErrorBanner(rateLimitErrorMessage);
        setIsLoading(false);
        return;
      }

      if (result.status >= 500) {
        setErrorBanner(internalServerErrorMessage);
        setIsLoading(false);
        return;
      }

      if (result.status === 0) {
        setErrorBanner("Network error. Please check your connection and try again.");
        setIsLoading(false);
        return;
      }

      setErrorBanner("An unexpected error occurred. Please try again.");
      setIsLoading(false);
    } catch (error) {
      setErrorBanner("Network error. Please check your connection and try again.");
      setIsLoading(false);
    }
  };

  return (
    <>
      <main className="login">
        {errorBanner && <ErrorAlert input={errorBanner} />}
        {successBanner && (
          <div className="alert alert-success" role="alert">
            {successBanner}
          </div>
        )}
        <div className="login-container">
          <div data-role="login-modals" className="login-modals">
            <div className="login-modal">
              <div className="login-modal-table">
                <div className="login-modal-table-row">
                  <div className="login-modal-body">
                    <div className="login-logo">
                      <img alt={LOGO_ALT} src={TWILIO_SENDGRID_LOGO} width="246" height="72" />
                    </div>

                    <div
                      className="login-form-title"
                      style={{ marginBottom: "20px", textAlign: "center" }}>
                      <h2>Forgot Username</h2>
                      <p style={{ fontSize: "14px", color: "#666" }}>
                        Enter your email and we&apos;ll send you a list of usernames associated with
                        it.
                      </p>
                    </div>

                    <form
                      data-role="forgot-username-form"
                      className="login-form"
                      onSubmit={formSubmit}>
                      <div className="row">
                        <div className="col-8 col-start-3">
                          <TextInput
                            label="Email Address"
                            id="email"
                            value={email}
                            type="email"
                            autoFocus={true}
                            autoComplete="username"
                            onChange={changeEmail}
                            containerRole="emailContainer"
                          />
                        </div>
                      </div>

                      <div className="login-btn" style={{ marginTop: "20px" }}>
                        <Button
                          dataRole="submit-btn"
                          className="btn btn-primary"
                          isLoading={isLoading}
                          isDisabled={isLoading || email.trim() === ""}>
                          Send Username(s)
                        </Button>
                      </div>
                    </form>

                    <div className="row" style={{ marginTop: "20px" }}>
                      <div className="col-12" style={{ textAlign: "center" }}>
                        <a
                          className="login-link"
                          href="/login/identifier"
                          style={{ textAlign: "center" }}>
                          Back to Login
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="login-modal-table-row">
                  <div className="login-modal-footer" data-role="login-modal-footer">
                    Don&apos;t have a Twilio SendGrid account?&nbsp;
                    <ExternalLink to={"/"} context={signupContext}>
                      Sign up now!
                    </ExternalLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
